// ===========================================
// Level Up Animation Component
// ===========================================
// Celebration modal for level advancement

import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { GamificationLevelInfo } from '../../types/gamification.types';
import MascotDisplay from './MascotDisplay';

interface LevelUpAnimationProps {
  open: boolean;
  onClose: () => void;
  previousLevel: GamificationLevelInfo;
  newLevel: GamificationLevelInfo;
  studentName?: string;
}

// Celebration animations
const confetti = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) rotate(0deg);
  }
  100% {
    opacity: 0;
    transform: translateY(100vh) rotate(720deg);
  }
`;

const bounce = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const shine = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const CONFETTI_COLORS = ['#FFD700', '#FF6B6B', '#4CAF50', '#2196F3', '#9C27B0', '#FF9800'];

export default function LevelUpAnimation({
  open,
  onClose,
  previousLevel,
  newLevel,
  studentName,
}: LevelUpAnimationProps) {
  const newColor = newLevel.color || '#4580E4';
  const newLevelName = newLevel.name;
  const prevLevelName = previousLevel.name;
  const mascotName = newLevel.mascot || 'Steve';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${newColor}20, #FCF6E6)`,
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
        }}
      >
        <Close />
      </IconButton>

      {/* Confetti */}
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100%',
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          {CONFETTI_COLORS.map((color, i) =>
            [0, 1, 2, 3].map((j) => (
              <Box
                key={`${i}-${j}`}
                sx={{
                  position: 'absolute',
                  top: -20,
                  left: `${(i * 6 + j * 4 + Math.random() * 10) * 3}%`,
                  width: 10,
                  height: 10,
                  backgroundColor: color,
                  borderRadius: j % 2 === 0 ? '50%' : 0,
                  animation: `${confetti} ${2 + Math.random()}s ease-out ${
                    Math.random() * 0.5
                  }s forwards`,
                }}
              />
            ))
          )}
        </Box>
      )}

      <DialogContent sx={{ textAlign: 'center', py: 4 }}>
        {/* Celebration Emoji */}
        <Typography
          sx={{
            fontSize: '4rem',
            mb: 2,
            animation: `${bounce} 0.5s ease-in-out infinite`,
          }}
        >
          🎉
        </Typography>

        {/* Level Up Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            background: `linear-gradient(90deg, ${newColor}, #FFD700, ${newColor})`,
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: `${shine} 2s linear infinite`,
          }}
        >
          LEVEL UP!
        </Typography>

        {/* Student Name */}
        {studentName && (
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            {studentName}
          </Typography>
        )}

        {/* Level Transition */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            mb: 3,
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', textDecoration: 'line-through' }}
          >
            {prevLevelName}
          </Typography>
          <Typography variant="h5" sx={{ color: 'text.primary' }}>
            →
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: newColor, fontWeight: 700 }}
          >
            {newLevelName}
          </Typography>
        </Box>

        {/* Mascot */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
            animation: `${bounce} 1s ease-in-out infinite`,
          }}
        >
          <MascotDisplay level={newLevel} size="large" showName showLevel={false} />
        </Box>

        {/* Congratulations Message */}
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Congratulations on reaching{' '}
          <Box component="span" sx={{ color: newColor, fontWeight: 600 }}>
            {newLevelName}
          </Box>
          !<br />
          {mascotName} is proud of your progress!
        </Typography>

        {/* Close Button */}
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: newColor,
            '&:hover': {
              backgroundColor: newColor,
              filter: 'brightness(0.9)',
            },
          }}
        >
          Continue Learning
        </Button>
      </DialogContent>
    </Dialog>
  );
}
